'use client';

import * as DS from '@aurorasolar/ds';
import Link from 'next/link';
import { ReactNode, useEffect } from 'react';

import { Subscribe } from '@/app/lp/components';

import { PostQuery } from '../../actions';
import { CategoriesList, Category } from '../../components';

import CreditBlock from './CreditBlock';
import SocialShare from './SocialShare';

const handleAnchorScroll = () => {
  const hash = window.location.hash.slice(1);
  const element = hash ? document.getElementById(hash) : document.getElementById('top');

  if (element) {
    element.scrollIntoView({ behavior: 'auto' });
  }
};

const Breadcrumbs = ({ post, ...props }: DS.SynthesizeProps<{ post: PostQuery['post'] }>) => {
  return (
    <DS.Flex flexDirection="row" gap={1} {...props}>
      <DS.Text>Blog</DS.Text>
      {/* spans prevent Google Translate DOM mutation from crashing React's reconciler */}
      <span>/</span>
      <DS.Button as={Link} color="uiLink" href="/home-solar/blog" variant="text">
        All Posts
      </DS.Button>
      <span>/</span>
      <DS.Text ellipse>{post?.title}</DS.Text>
    </DS.Flex>
  );
};

type Props = {
  categories: Category[];
  children: ReactNode;
  post: PostQuery['post'];
};

export const PostContent = ({ categories, children, post }: Props) => {
  const standaloneBlog = post?.categories?.nodes?.every(({ slug }) => slug?.toLowerCase() !== 'uncategorized');

  // Handles scroll to anchor links - Next.js doesn't have native support for this
  useEffect(() => {
    window.addEventListener('popstate', handleAnchorScroll);

    return () => {
      window.removeEventListener('popstate', handleAnchorScroll);
    };
  }, []);

  return (
    <DS.Flex flexDirection="row" gap={{ _: 0, md: 8 }} maxW={DS.BorealisTheme.screens.xl} mx="auto" px={4}>
      {standaloneBlog ? (
        <DS.Flex display={{ _: 'none', md: 'flex' }} flexShrink={0} gap={6} h="100%" maxWidth={320} pt={3} w="25%">
          <DS.Divider />
          <Breadcrumbs post={post} />
          <DS.Divider />
          <CategoriesList categories={categories} />
          <DS.Divider />
          <DS.Flex>
            <DS.Text>Get updates in your inbox</DS.Text>
            <DS.Box py={6}>
              <Subscribe icon={DS.IconArrowRight} variant="primary" />
            </DS.Box>
            <DS.Text>Subscribe and stay up to speed on the latest at Aurora.</DS.Text>
          </DS.Flex>
        </DS.Flex>
      ) : null}
      <DS.Flex
        flexDirection="column"
        flexShrink={1}
        id="content"
        maxW={{ _: '100%', md: '65%' }}
        mx={standaloneBlog ? 0 : 'auto'}
        py={6}
      >
        {standaloneBlog ? (
          <DS.Flex alignItems="center" flexDirection={{ _: 'column', md: 'row' }} justifyContent="space-between">
            <Breadcrumbs display={{ _: 'flex', md: 'none' }} post={post} />
            <CreditBlock
              author={post?.blogFields?.authorName}
              date={post?.date}
              image={post?.blogFields?.authorImage?.node}
            />
            <SocialShare />
            <DS.Divider display={{ _: 'block', md: 'none' }} my="4" />
          </DS.Flex>
        ) : null}

        {/* notranslate: in-app browsers (notably the Google iOS app) auto-translate the page,
            which mutates this parsed-HTML subtree out from under React and crashes the reconciler
            with NotFoundError / "Maximum call stack size exceeded" (CONS-2490). Opt the article body
            out of on-the-fly translation; full-page translate.google.com still works. */}
        <DS.Flex className="notranslate" translate="no">
          {children}
        </DS.Flex>

        {standaloneBlog ? (
          <>
            <DS.Divider my="4" />
            <DS.Flex alignItems="center" flexDirection={{ _: 'column', md: 'row' }} justifyContent="space-between">
              <CreditBlock
                author={post?.blogFields?.authorName}
                date={post?.date}
                image={post?.blogFields?.authorImage?.node}
              />
              <SocialShare />
            </DS.Flex>
            <DS.Divider my="4" />
          </>
        ) : null}
        <DS.Flex flexDirection="row" justifyContent="end">
          <DS.Button as={Link} href="/home-solar/blog" icon={DS.IconArrowRight} iconPosition="right" variant="text">
            All Posts
          </DS.Button>
        </DS.Flex>
      </DS.Flex>
    </DS.Flex>
  );
};
